import { useContext } from 'react';

import { sendAnalytics } from '@storefront/app/analytics/SimpleTriggerEvent';
import { FlowType } from '@storefront/app/flow-orchestrator/types';
import { FaqCancellationTable } from '@storefront/app/pdp-config/shared-components/faq/faq-cancellation-table/faq-cancellation-table';
import { IFaqType } from '@storefront/app/pdp-config/shared-components/faq/faq.utils';
import { FlagsContext } from '@storefront/context/LDFlagsContext';
import { BundleProduct } from '@storefront/data-commerce';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import { TranslatedText } from '@storefront/shared-components/translated-text/translated-text';
import { FeatureFlagData } from '@storefront/types/featureFlagData';
import { Cancel } from '@storefront/types/Product';

import styles from './../app/pdp-config/shared-components/faq/faq.module.scss';
import { NamespacesI18n } from '@storefront/i18n';
import { useTranslation } from 'react-i18next';
import { applyFeatureFlagI18nData } from '@storefront/app/pdp-config/printer/utils/utils';
import { FlowOrchestratorContext } from '@storefront/app/flow-orchestrator/context';
import { i18n } from 'i18next';

export interface FaqQuestionAndAnswer {
  question: string;
  answer: string;
  table: TableType;
  list: any;
  answerComplement: string;
  centralizedAnswer?: boolean;
  links?: Array<{ [key: string]: string }> | [];
  featureFlags?: FeatureFlagData<FaqQuestionAndAnswer>[] | [];
}
export type AnchorEvent =
  | React.MouseEvent<HTMLAnchorElement, MouseEvent>
  | React.KeyboardEvent<HTMLAnchorElement>;

interface GetFaqsProps {
  flowType: FlowType;
  cancellationList: Cancel[];
  multipleProduct: BundleProduct[];
  skus: string[];
  hasThreeYearsCancellationSku: boolean;
}
export interface LinksType {
  href: string;
  id: string;
  'data-testid': string;
  onClick: (event: AnchorEvent) => void;
}

enum TableType {
  CANCELLATION = 'cancellationTable',
  UNRETURNED_PRINTER_FEE = 'unreturnedPrinterFeeTable',
}

export const getFaqQuestions = (i18n: i18n, flow: string, v2 = false) => {
  const getFaqQuestions = i18n.getResource(
    i18n.language,
    NamespacesI18n.PDP_CONFIG,
    `faq${v2 ? 'V2' : ''}.${flow}.questions`
  );
  const flags = useContext(FlagsContext);
  const questionsFromJson = Object.values(getFaqQuestions);

  const faqQuestions = questionsFromJson
    .map((faqItem) => applyFeatureFlagI18nData(faqItem, flags))
    .filter((item): item is FaqQuestionAndAnswer => item !== null);

  const sendAnalyticsHLC = (href: string, controlName: string) => {
    const productAnalytics = uiEvents.faqGenericHLCprinter;
    sendAnalytics({
      simpleUiEvent: { ...productAnalytics, controlName, controlDetail: href },
    });
  };

  const reducedLinks = faqQuestions.reduce<Record<string, LinksType>>(
    (acc, item) => {
      if (
        typeof item.links === 'object' &&
        Object.keys(item.links).length > 0
      ) {
        Object.entries(item.links).forEach(([key, value]) => {
          acc[key] = {
            href: value.href,
            id: `faq-${key}-link`,
            'data-testid': `faq-${key}-link-test`,
            onClick: (event: AnchorEvent) =>
              sendAnalyticsHLC(event.currentTarget.href, value.controlName),
          };
        });
      }
      return acc;
    },
    {}
  );
  return { reducedLinks, faqQuestions };
};

export const useGetFaqs = ({
  flowType,
  cancellationList,
  hasThreeYearsCancellationSku,
}: GetFaqsProps): Array<IFaqType> => {
  const { i18n } = useTranslation(NamespacesI18n.PDP_CONFIG);
  const { isSMB } = useContext(FlowOrchestratorContext);

  let flow: string;
  if (flowType === FlowType.Pc) {
    flow = 'pc';
  } else if (isSMB) {
    flow = 'smbPrinter';
  } else {
    flow = 'printer';
  }

  const { reducedLinks, faqQuestions } = getFaqQuestions(i18n, flow);

  const renderTableByType = (table: TableType, answerComplement: string) => {
    const tableComponents = {
      [TableType.CANCELLATION]: (
        <>
          <FaqCancellationTable
            hasThreeYearsCancellationSku={hasThreeYearsCancellationSku}
            cancellationList={cancellationList}
          />
          <p className={styles['table-complement']}>
            <TranslatedText
              linkStyle="black"
              i18nKey={answerComplement}
              links={reducedLinks}
            />
          </p>
        </>
      ),
      [TableType.UNRETURNED_PRINTER_FEE]: (
        <FaqCancellationTable
          unreturnedPrinterFee
          cancellationList={cancellationList}
        />
      ),
    };

    return tableComponents[table] || null;
  };

  return faqQuestions.map(
    ({ question, answer, table, list, answerComplement }, index) => ({
      expanded: false,
      header: {
        centralArea: <span>{question}</span>,
      },
      content: (
        <div>
          <TranslatedText
            linkStyle="black"
            i18nKey={answer}
            links={reducedLinks}
          />
          {table && renderTableByType(table, answerComplement)}

          {list && (
            <div className={styles['styled-list']}>
              {faqQuestions.map(({ list }, questionIndex) =>
                list ? (
                  <ul key={`list-${faqQuestions[questionIndex]?.question}`}>
                    {list.map((item: string) => (
                      <li key={item} className="body-large">
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : null
              )}
            </div>
          )}
        </div>
      ),
      id: `question_${index}`,
    })
  );
};
